import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, GripVertical } from "lucide-react";

export default function TaskSettings() {
  const [cats, setCats] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [newStatus, setNewStatus] = useState("");

  const load = () => Promise.all([api.get("/task-categories"), api.get("/task-statuses")])
    .then(([c, s]) => { setCats(c.data.items); setStatuses(s.data.items); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const addCat = async () => { if (!newCat.trim()) return; try { await api.post("/task-categories", { label: newCat.trim() }); setNewCat(""); load(); } catch (e) { toast.error(apiError(e)); } };
  const addStatus = async () => { if (!newStatus.trim()) return; try { await api.post("/task-statuses", { label: newStatus.trim() }); setNewStatus(""); load(); } catch (e) { toast.error(apiError(e)); } };
  const toggleCat = async (c) => { await api.patch(`/task-categories/${c.id}`, { active: c.active === false }); load(); };
  const toggleStatus = async (s) => { await api.patch(`/task-statuses/${s.id}`, { active: s.active === false }); load(); };

  return (
    <div className="max-w-3xl">
      <PageHeader title="Task Management Settings" description="Configure task categories and statuses for your account." />
      <div className="grid gap-5 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="task-categories-config">
          <h3 className="font-semibold text-slate-800 mb-3">Task Categories</h3>
          <div className="space-y-1.5 mb-3">
            {cats.filter((c) => !c.personal).map((c) => (
              <div key={c.id} className="flex items-center gap-2 py-1.5 border-b border-slate-50" data-testid={`cat-${c.id}`}>
                <GripVertical className="w-3.5 h-3.5 text-slate-300" />
                <span className={`text-sm flex-1 ${c.active === false ? "text-slate-300 line-through" : "text-slate-700"}`}>{c.label}</span>
                {c.is_system && <span className="text-[10px] text-slate-400">system</span>}
                <button onClick={() => toggleCat(c)} data-testid={`cat-toggle-${c.id}`} className="text-xs text-brand hover:underline">{c.active === false ? "Activate" : "Deactivate"}</button>
              </div>
            ))}
          </div>
          <div className="flex gap-2"><Input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()} placeholder="New category" className="h-9" data-testid="new-category-input" />
            <Button size="sm" variant="outline" onClick={addCat} data-testid="add-category"><Plus className="w-4 h-4" /></Button></div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="task-statuses-config">
          <h3 className="font-semibold text-slate-800 mb-3">Task Statuses</h3>
          <div className="space-y-1.5 mb-3">
            {statuses.map((s) => (
              <div key={s.id} className="flex items-center gap-2 py-1.5 border-b border-slate-50" data-testid={`status-${s.id}`}>
                <GripVertical className="w-3.5 h-3.5 text-slate-300" />
                <span className={`text-sm flex-1 ${s.active === false ? "text-slate-300 line-through" : "text-slate-700"}`}>{s.label}</span>
                {s.is_system && <span className="text-[10px] text-slate-400">system</span>}
                <button onClick={() => toggleStatus(s)} data-testid={`status-toggle-${s.id}`} className="text-xs text-brand hover:underline">{s.active === false ? "Activate" : "Deactivate"}</button>
              </div>
            ))}
          </div>
          <div className="flex gap-2"><Input value={newStatus} onChange={(e) => setNewStatus(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addStatus()} placeholder="New status" className="h-9" data-testid="new-status-input" />
            <Button size="sm" variant="outline" onClick={addStatus} data-testid="add-status"><Plus className="w-4 h-4" /></Button></div>
        </div>
      </div>
    </div>
  );
}
